// Durable storage layer.
//
// On web: localStorage (synchronous, fast).
// On Capacitor native: localStorage *plus* a write-through mirror to
// @capacitor/preferences, which is backed by UserDefaults (iOS) /
// SharedPreferences (Android). This matters because iOS may evict WKWebView
// localStorage when the device is low on space — Preferences survives.
//
// Reads stay synchronous so the existing store doesn't need rewriting.
// On boot, hydrate() reconciles Preferences → localStorage if the
// Preferences copy is non-empty and the localStorage copy is missing.

import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

let prefsPromise = null;
function loadPrefs() {
  if (!isNative()) return Promise.resolve(null);
  if (!prefsPromise) {
    prefsPromise = import('@capacitor/preferences')
      .then((mod) => mod.Preferences)
      .catch(() => null);
  }
  return prefsPromise;
}

export function readSync(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function writeSync(key, value) {
  try { localStorage.setItem(key, value); } catch {}
  // Fire-and-forget mirror to native Preferences.
  if (isNative()) {
    loadPrefs().then((prefs) => {
      if (prefs) prefs.set({ key, value }).catch(() => {});
    });
  }
}

// Async: reconcile native Preferences into localStorage if local copy missing.
// Returns true if the in-memory state should be reloaded from localStorage.
export async function hydrateFromNative(key) {
  if (!isNative()) return false;
  const prefs = await loadPrefs();
  if (!prefs) return false;
  try {
    const local = readSync(key);
    if (local) return false; // localStorage has data; trust it
    const { value } = await prefs.get({ key });
    if (value) {
      try { localStorage.setItem(key, value); } catch {}
      return true;
    }
  } catch {}
  return false;
}
